import type * as React from "react"
import { DayPicker, getDefaultClassNames } from "react-day-picker"
import { cn } from "../../lib/util"

export type CalendarProps = React.ComponentProps<typeof DayPicker>

// Rose theming layered on top of react-day-picker's default styles. We merge
// with getDefaultClassNames() so all of the library's layout/sizing (44px touch
// targets, circular day buttons, grid) is preserved — we only restyle colour,
// typography and hover/selected states to match the rest of the booking flow.
function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
  const dcn = getDefaultClassNames()

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      style={
        {
          "--rdp-accent-color": "#B87775",
          "--rdp-accent-background-color": "#FBEFEF",
          "--rdp-today-color": "#B87775",
          "--rdp-selected-border": "2px solid #B87775",
          "--rdp-day_button-border-radius": "9999px",
        } as React.CSSProperties
      }
      classNames={{
        month_caption: cn(dcn.month_caption, "h-10"),
        caption_label: cn(dcn.caption_label, "font-serif text-lg text-[#3a2e2c]"),
        button_previous: cn(
          dcn.button_previous,
          "rounded-full text-[#B87775] transition-colors hover:bg-[#FBEFEF]"
        ),
        button_next: cn(
          dcn.button_next,
          "rounded-full text-[#B87775] transition-colors hover:bg-[#FBEFEF]"
        ),
        chevron: cn(dcn.chevron, "fill-[#B87775]"),
        weekday: cn(dcn.weekday, "text-[#9A6B69] font-normal"),
        day_button: cn(
          dcn.day_button,
          "text-[#3a2e2c] transition-colors hover:bg-[#FBEFEF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775]"
        ),
        today: cn(dcn.today, "font-semibold"),
        // Filled rose circle for the chosen day; important beats the default
        // border-only selected rule.
        selected: cn(
          dcn.selected,
          "[&>button]:!border-transparent [&>button]:!bg-[#B87775] [&>button]:!text-white"
        ),
        ...classNames,
      }}
      {...props}
    />
  )
}
Calendar.displayName = "Calendar"

export { Calendar }
